import { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Drawer, Typography } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
import { useProject } from '../../hooks/ProjectContext';
import { useRole } from '../../hooks/useRole';
//
import Header from './header';
import Nav from './nav';
import { MapView } from '../../components/map';
import ChatWidget from '../../components/chat/ChatWidget';

// Set to false to fall back to the GEE iframe viewer
const USE_LEAFLET_MAP = true;

// ----------------------------------------------------------------------

const HEADER_HEIGHT = 64;
const NAV_WIDTH = 280;
const NAV_COLLAPSE_WIDTH = 88;
const VIEWER_URL = 'https://terrra-iot.projects.earthengine.app/view/viewer';

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: HEADER_HEIGHT + 24,
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.up('lg')]: {
    paddingTop: HEADER_HEIGHT + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const MapMain = styled('div')({
  flexGrow: 1,
  height: '100vh',
  paddingTop: HEADER_HEIGHT,
  overflow: 'hidden',
});

// ----------------------------------------------------------------------

export default function ProjectLayout() {
  const [open, setOpen] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  // ?map=1 deep-links straight into the map (visual mode) — used by the field
  // capture tool's "see on map" links; &evidence=<id> is consumed by MapView.
  const [visualMode, setVisualMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('map') === '1'; }
    catch { return false; }
  });
  const [chatOpen, setChatOpen] = useState(false);
  const isDesktop = useResponsive('up', 'lg');
  const location = useLocation();
  const { selectedProject, selectedSite, availableProjects } = useProject();
  const { role } = useRole();
  const iframeRef = useRef(null);
  const dataSentRef = useRef(false);
  const fallbackTimerRef = useRef(null);

  const navWidth = isDesktop ? (isNavCollapsed ? NAV_COLLAPSE_WIDTH : NAV_WIDTH) : NAV_WIDTH;

  const getPageTitle = () => {
    if (visualMode) return 'Map View';
    if (location.pathname.includes('/project/sampling')) return 'Sampling';
    if (location.pathname.includes('/project/stratification')) return 'Stratification';
    if (location.pathname.includes('/project/capture')) return 'Capture';
    if (location.pathname.includes('/project/site-visit')) return 'Site Visit';
    return 'Project';
  };

  const viewerUrl = selectedProject?.code
    ? `${VIEWER_URL}?project=${selectedProject.code}${selectedSite?.code ? '.' + selectedSite.code : ''}`
    : VIEWER_URL;

  const sendDataToViewer = useCallback(async () => {
    if (!iframeRef.current || !selectedProject?.code) return;
    const win = iframeRef.current.contentWindow;
    const siteQuery = selectedSite?.code ? `&site=${selectedSite.code}` : '';

    // 1. ROI geometry (boundaries)
    try {
      const res = await fetch(`/v1/stratification/geometry?project=${selectedProject.code}${siteQuery}`);
      if (res.ok) {
        const geometry = await res.json();
        win.postMessage(JSON.stringify({ type: 'set_roi', geometry }), '*');
      }
    } catch (_) {}

    // 2. Field evidence
    try {
      const res = await fetch(`/v1/evidence?projectId=${selectedProject.id}${selectedSite?.id ? `&siteId=${selectedSite.id}` : ''}`);
      if (res.ok) {
        const items = await res.json();
        const features = items
          .filter((e) => e.latitude && e.longitude)
          .map((e) => ({ lat: parseFloat(e.latitude), lng: parseFloat(e.longitude), title: e.category || '' }));
        if (features.length) {
          win.postMessage(JSON.stringify({ type: 'display_evidence', features }), '*');
        }
      }
    } catch (_) {}

    // 3. Latest strata + sampling artifacts (single fetch)
    try {
      const res = await fetch(`/v1/stratification/artifacts?project=${selectedProject.code}`);
      if (res.ok) {
        const artifacts = await res.json();

        // Stratification output only — uploaded field-geometry KMLs also carry
        // subtype 'strata' and must not masquerade as display_strata in the viewer.
        const strataArt = artifacts.filter((a) => a.tool === 'stratification' && a.subtype === 'strata').sort((a, b) => b.id - a.id)[0];
        if (strataArt) {
          const r = await fetch(`/v1/stratification/artifacts/${strataArt.id}`);
          if (r.ok) {
            const data = await r.json();
            const features = data.features || data;
            if (features.length) win.postMessage(JSON.stringify({ type: 'display_strata', features }), '*');
          }
        }

        const ptsArt = artifacts.filter((a) => a.subtype === 'points').sort((a, b) => b.id - a.id)[0];
        if (ptsArt) {
          const r = await fetch(`/v1/stratification/artifacts/${ptsArt.id}`);
          if (r.ok) {
            const data = await r.json();
            const features = (data.features || data).filter((f) => f.geometry?.type === 'Point');
            if (features.length) win.postMessage(JSON.stringify({ type: 'display_points', features }), '*');
          }
        }
      }
    } catch (_) {}
  }, [selectedProject, selectedSite]);

  // Listen for gee_ready from the viewer iframe
  useEffect(() => {
    if (!visualMode) {
      dataSentRef.current = false;
      clearTimeout(fallbackTimerRef.current);
      return;
    }
    const handler = (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'gee_ready' && !dataSentRef.current) {
          dataSentRef.current = true;
          clearTimeout(fallbackTimerRef.current);
          sendDataToViewer();
        }
      } catch (_) {}
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [visualMode, sendDataToViewer]);

  const handleIframeLoad = useCallback(() => {
    // Fallback: if gee_ready never arrives, send data 8s after iframe load
    clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = setTimeout(() => {
      if (!dataSentRef.current) {
        dataSentRef.current = true;
        sendDataToViewer();
      }
    }, 8000);
  }, [sendDataToViewer]);

  return (
    <StyledRoot>
      <Header
        onOpenNav={() => setOpen(true)}
        navWidth={visualMode ? 0 : navWidth}
        isNavCollapsed={isNavCollapsed}
        onToggleNavCollapse={() => setIsNavCollapsed((v) => !v)}
        pageTitle={getPageTitle()}
        visualMode={visualMode}
        onExitVisualMode={() => setVisualMode(false)}
        onOpenChat={() => setChatOpen(true)}
      />

      {!visualMode && (
        <Nav
          openNav={open}
          onCloseNav={() => setOpen(false)}
          isNavCollapsed={isNavCollapsed}
          onLogoClick={() => setVisualMode(true)}
          visualMode={visualMode}
        />
      )}

      {visualMode ? (
        <MapMain>
          {USE_LEAFLET_MAP ? (
            <MapView
              key={`${selectedProject?.code}-${selectedSite?.code}`}
              projectCode={selectedProject?.code}
              projectId={selectedProject?.id}
              siteCode={selectedSite?.code}
              siteId={selectedSite?.id}
              onClose={() => setVisualMode(false)}
            />
          ) : (
            <iframe
              ref={iframeRef}
              key={viewerUrl}
              src={viewerUrl}
              title="Project Map"
              onLoad={handleIframeLoad}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          )}
        </MapMain>
      ) : (
        <Main>
          {!role && availableProjects.length === 0
            ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, textAlign: 'center', px: 3 }}>
                <Typography variant="h5" sx={{ mb: 1 }}>No Projects Assigned</Typography>
                <Typography variant="body1" color="text.secondary">
                  Your account hasn't been assigned to any project yet.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Ask a Project Manager from your organization to assign you, or contact your Admin.
                </Typography>
              </Box>
            )
            : <Outlet />
          }
        </Main>
      )}
      {/* AI chat — right drawer, activated from header icon */}
      <Drawer anchor="right" open={chatOpen} onClose={() => setChatOpen(false)} PaperProps={{ sx: { width: 400, maxWidth: '90vw' } }}>
        <ChatWidget visualMode={visualMode} embedded onClose={() => setChatOpen(false)} />
      </Drawer>
    </StyledRoot>
  );
}
